// src/components/ads-admin/useAdsAdminController.js
import { useEffect, useMemo, useRef, useState } from "react";
import {
  adsHealth,
  adsListBrands,
  adsStartIngestion,
  adsListRuns,
  adsListLogs,
  adsFinalizeRun,
  adsListEntities,
  adsUpsertEntity,
  adsDeleteEntity,
  adsListBrandAds,
  adsExportBrandAdsCsv,
  adsPreloadSnapshots,
  adsGetAd,
  adsValidateProvider,
  adsExecuteIngestion,
  adsGetRunStats,
  adsSearchProviderEntities,
  adsListCompetitors,
  adsAddCompetitor,
  adsRemoveCompetitor,
  adsDeleteAdsBatch,
} from "../../services/api";

import { getBrandId, sanitizeSource } from "./utils";

export default function useAdsAdminController({ showToast }) {
  const browseRef = useRef(null);

  const autoAdvancedRef = useRef(false);

  const LS = "ads.admin.state";
  const _ls = (() => { try { return JSON.parse(localStorage.getItem(LS) || "{}"); } catch { return {}; } })();

  // Filters are now keyed by catalogue field. Earlier builds stored about
  // twenty named values instead, both in local storage and inside saved
  // presets; this maps them across so nothing a user set up is lost.
  const legacyToFieldFilters = (f = {}) => {
    const out = {};
    const one = (v) => (v ? [String(v)] : undefined);
    const range = (from, to) => (from || to ? { from: from || undefined, to: to || undefined } : undefined);
    const put = (k, v) => { if (v !== undefined && v !== "") out[k] = v; };
    put("start_date", range(f.dateFrom, f.dateTo));
    put("end_date", range(f.endDateFrom, f.endDateTo));
    put("status", one(f.status));
    put("creative_type", one(f.creativeType));
    put("headline", f.headline || undefined);
    put("advertiser_ref", f.advertiser || undefined);
    put("advertiser_name", f.advertiserName || undefined);
    put("call_to_action", f.cta || undefined);
    put("primary_text", f.body || undefined);
    put("ai.composition.color_palette", one(f.visualPalette));
    put("ai.marketing_interpretation.funnel_stage", one(f.visualFunnel));
    put("ai.marketing_interpretation.claim_types", one(f.visualClaim));
    if (f.peoplePresent === "true" || f.peoplePresent === true) out["ai.people.total_count"] = { min: 1 };
    if (f.peoplePresent === "false" || f.peoplePresent === false) out["ai.people.total_count"] = { max: 0 };
    put("publisher_platforms", one(f.publisherPlatform && String(f.publisherPlatform).toLowerCase()));
    put("languages", one(f.language));
    put("target_gender", one(f.targetGender));
    if (f.euReachMin) out.eu_total_reach = { min: f.euReachMin };
    put("target_domain", f.targetDomain || undefined);
    put("platforms", one(f.googlePlatform));
    return out;
  };

  const _legacyLsShape = {
    dateFrom: _ls.filterDateFrom, dateTo: _ls.filterDateTo,
    endDateFrom: _ls.filterEndDateFrom, endDateTo: _ls.filterEndDateTo,
    status: _ls.filterStatus, creativeType: _ls.filterCreativeType,
    headline: _ls.filterHeadline, advertiser: _ls.filterAdvertiser,
    advertiserName: _ls.filterAdvertiserName, cta: _ls.filterCta, body: _ls.filterBody,
    visualPalette: _ls.filterVisualPalette, visualFunnel: _ls.filterVisualFunnel,
    visualClaim: _ls.filterVisualClaim, peoplePresent: _ls.filterPeoplePresent,
    publisherPlatform: _ls.filterPublisherPlatform, language: _ls.filterLanguage,
    targetGender: _ls.filterTargetGender, euReachMin: _ls.filterEuReachMin,
    targetDomain: _ls.filterTargetDomain, googlePlatform: _ls.filterGooglePlatform,
  };
  const [fieldFilters, setFieldFilters] = useState(
    () => _ls.fieldFilters || legacyToFieldFilters(_legacyLsShape)
  );

  const [step, _setStep] = useState(() => Math.max(1, Math.min(4, Number(_ls.step) || 1)));
  const setStep = (v) => { _setStep(v); try { const s = JSON.parse(localStorage.getItem(LS) || "{}"); localStorage.setItem(LS, JSON.stringify({ ...s, step: v })); } catch {} };

  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [brandId, _setBrandId] = useState(() => String(_ls.brandId || ""));
  const [competitors, setCompetitors] = useState([]); // [{competitor_brand_id, competitor_brand_name}]
  const setBrandId = (v) => { _setBrandId(v); try { const s = JSON.parse(localStorage.getItem(LS) || "{}"); localStorage.setItem(LS, JSON.stringify({ ...s, brandId: v })); } catch {} };

  // Remembered like the brand, step and view mode, so a refresh keeps the
  // provider being looked at instead of dropping back to Meta.
  const [source, _setSource] = useState(() => sanitizeSource(_ls.source || "meta"));
  const setSource = (v) => { _setSource(v); try { const s = JSON.parse(localStorage.getItem(LS) || "{}"); localStorage.setItem(LS, JSON.stringify({ ...s, source: v })); } catch {} };

  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // IMPORTANT: entities is ALWAYS the full set for the brand (all providers).
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  // competitorEntities: { [brandId]: entity[] }
  const [competitorEntities, setCompetitorEntities] = useState({});
  const [newEntityRef, setNewEntityRef] = useState("");
  const [savingEntity, setSavingEntity] = useState(false);
  const [deletingEntityId, setDeletingEntityId] = useState(null);

  const [validatingKey, setValidatingKey] = useState(null);
  const [bulkValidating, setBulkValidating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const [fetchDateFrom, setFetchDateFrom] = useState("");
  const [fetchDateTo, setFetchDateTo] = useState("");

  const [searchQ, setSearchQ] = useState("");
  const [searchItems, setSearchItems] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [searchOpen, setSearchOpen] = useState(true);
  const [searchPage, setSearchPage] = useState(0);

  const [starting, setStarting] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // ✅ show run immediately in Step 3 while backend executes
  const [currentRunId, setCurrentRunId] = useState(null);
  const [currentRunSource, setCurrentRunSource] = useState(null);

  const [runStats, setRunStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [adsLimit, setAdsLimit] = useState(50);
  const [adsOffset, setAdsOffset] = useState(0);
  const [adsFullscreen, setAdsFullscreen] = useState(false);
  const [viewMode, _setViewMode] = useState(() => _ls.viewMode || "list");
  const setViewMode = (v) => { _setViewMode(v); try { const s = JSON.parse(localStorage.getItem(LS) || "{}"); localStorage.setItem(LS, JSON.stringify({ ...s, viewMode: v })); } catch {} };

  // Ads filters - hydrated from localStorage (ADS040)
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterEndDateFrom, setFilterEndDateFrom] = useState("");
  const [filterEndDateTo, setFilterEndDateTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCreativeType, setFilterCreativeType] = useState("");
  const [filterHeadline, setFilterHeadline] = useState("");
  const [filterAdvertiser, setFilterAdvertiser] = useState("");
  const [filterAdvertiserName, setFilterAdvertiserName] = useState("");
  const [filterCta, setFilterCta] = useState("");
  const [filterBody, setFilterBody] = useState("");
  // Visual-attribute filters (E4.5) - backed by ad_creatives.visual_attributes_json
  const [filterVisualPalette, setFilterVisualPalette] = useState("");
  const [filterVisualFunnel, setFilterVisualFunnel] = useState("");
  const [filterVisualClaim, setFilterVisualClaim] = useState("");
  const [filterPeoplePresent, setFilterPeoplePresent] = useState(""); // "" | "true" | "false"
  const [filterNewDays, setFilterNewDays] = useState(() => _ls.filterNewDays || "");                   // "" | "7" | "30"
  // Versions: view every ad as it was captured by a date, and/or keep only ads
  // no run has fetched since a date. Both "YYYY-MM-DD" or "".
  const [filterAsOf, setFilterAsOf] = useState(() => _ls.filterAsOf || "");
  const [filterNotUpdatedSince, setFilterNotUpdatedSince] = useState(() => _ls.filterNotUpdatedSince || "");
  // A period the ads were live in, for like-for-like comparison between advertisers.
  const [filterRunningFrom, setFilterRunningFrom] = useState(() => _ls.filterRunningFrom || "");
  const [filterRunningTo, setFilterRunningTo] = useState(() => _ls.filterRunningTo || "");
  // Provider-attribute filters (ads12) - backed by columns on `ads`
  const [filterPublisherPlatform, setFilterPublisherPlatform] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("");
  const [filterTargetGender, setFilterTargetGender] = useState("");
  const [filterEuReachMin, setFilterEuReachMin] = useState("");
  const [filterTargetDomain, setFilterTargetDomain] = useState("");
  const [filterGooglePlatform, setFilterGooglePlatform] = useState("");
  // Narrows the collection to one run's contribution.
  const [filterIngestionRunId, setFilterIngestionRunId] = useState("");
  const [adsTotal, setAdsTotal] = useState(0);

  // The complete live filter state, in api.js key form. Single source for
  // the list view AND the CSV export, so the two can no longer disagree.
  const currentFilterKeys = () => ({
    dateFrom: filterDateFrom || undefined,
    dateTo: filterDateTo || undefined,
    endDateFrom: filterEndDateFrom || undefined,
    endDateTo: filterEndDateTo || undefined,
    status: filterStatus || undefined,
    creativeType: filterCreativeType || undefined,
    headline: filterHeadline || undefined,
    advertiser: filterAdvertiser || undefined,
    advertiserName: filterAdvertiserName || undefined,
    cta: filterCta || undefined,
    body: filterBody || undefined,
    visualPalette: filterVisualPalette || undefined,
    visualFunnel: filterVisualFunnel || undefined,
    visualClaim: filterVisualClaim || undefined,
    peoplePresent: filterPeoplePresent === "" ? undefined : filterPeoplePresent === "true",
    asOf: filterAsOf || undefined,
    notUpdatedSince: filterNotUpdatedSince || undefined,
    runningFrom: filterRunningFrom || undefined,
    runningTo: filterRunningTo || undefined,
    newSince: filterNewDays
      ? new Date(Date.now() - Number(filterNewDays) * 86400000).toISOString()
      : undefined,
    publisherPlatform: filterPublisherPlatform || undefined,
    language: filterLanguage || undefined,
    targetGender: filterTargetGender || undefined,
    euReachMin: filterEuReachMin || undefined,
    targetDomain: filterTargetDomain || undefined,
    googlePlatform: filterGooglePlatform || undefined,
    firstIngestionRunId: filterIngestionRunId || undefined,
    fieldFilters: _compactFieldFilters(fieldFilters),
  });

  function _compactFieldFilters(ff) {
    const out = {};
    for (const [k, v] of Object.entries(ff || {})) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v) && !v.length) continue;
      if (typeof v === "object" && !Array.isArray(v)
          && Object.values(v).every(x => x === undefined || x === null || x === "")) continue;
      out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  }

  // Saved named filters (ADS040-v2)
  const LS_SAVED = "ads.saved.filters";
  const [savedFilters, setSavedFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ads.saved.filters") || "[]"); } catch { return []; }
  });
  const _persistSavedFilters = (list) => { try { localStorage.setItem(LS_SAVED, JSON.stringify(list)); } catch {} };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [loadingAd, setLoadingAd] = useState(false);

  const [showAdvancedRuns, setShowAdvancedRuns] = useState(false);
  const [showAdvancedLogs, setShowAdvancedLogs] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);

  const apiStatus = health?.status ? String(health.status) : "unknown";
  const apiTone = apiStatus === "ok" ? "success" : "neutral";

  const selectedBrand = useMemo(() => {
    const id = Number(brandId);
    if (!id) return null;
    return brands.find((b) => Number(getBrandId(b)) === id) || brands.find((b) => Number(b?.id) === id) || null;
  }, [brandId, brands]);

  // ---------- entities by source (provider-level snapshot must NOT depend on selected provider) ----------
  const entitiesBySource = useMemo(() => {
    const out = { meta: [], google: [], tiktok: [] };
    for (const e of entities || []) {
      const s = sanitizeSource(e?.source);
      if (!out[s]) out[s] = [];
      out[s].push(e);
    }
    return out;
  }, [entities]);

  const entitiesForSource = useMemo(() => {
    const src = sanitizeSource(source);
    return (entitiesBySource[src] || []).slice();
  }, [entitiesBySource, source]);

  const activeEntities = useMemo(() => (entitiesForSource || []).filter((e) => !!e?.active), [entitiesForSource]);
  const validatedActiveEntities = useMemo(
    () => (activeEntities || []).filter((e) => e?.last_validation_ok === true),
    [activeEntities]
  );

  const runsSorted = useMemo(() => {
    const arr = Array.isArray(runs) ? runs.slice() : [];
    arr.sort((a, b) => {
      const at = new Date(b?.started_at || b?.created_at || 0).getTime();
      const bt = new Date(a?.started_at || a?.created_at || 0).getTime();
      return at - bt;
    });
    return arr;
  }, [runs]);

  const lastRunForSource = useMemo(() => {
    const src = sanitizeSource(source);
    const match = (runsSorted || []).find((r) => String(r?.source || "").toLowerCase() === src);
    return match || (runsSorted || [])[0] || null;
  }, [runsSorted, source]);

  // Any successful run for this provider, not only the latest: the ads a run
  // stored stay browsable when a later run fails. Checking the latest alone
  // locked Browse Ads over 607 stored ads after one failed run (2026-10-08).
  const hasSuccessfulRunForSource = useMemo(() => {
    const src = sanitizeSource(source);
    return (runsSorted || []).some((r) => {
      const s = String(r?.status || "").toLowerCase();
      return !!r?.id && String(r?.source || "").toLowerCase() === src
        && (s.includes("success") || s.includes("done") || s.includes("completed"));
    });
  }, [runsSorted, source]);

  const canProceedStep2 = !!brandId && !!source;
  const canProceedStep3 = canProceedStep2 && validatedActiveEntities.length > 0;
  const canProceedStep4 = canProceedStep3 && hasSuccessfulRunForSource;

  const maxStepAllowed = useMemo(() => {
    if (canProceedStep4) return 4;
    if (canProceedStep3) return 3;
    if (canProceedStep2) return 2;
    return 1;
  }, [canProceedStep2, canProceedStep3, canProceedStep4]);

  // ---------------- API loaders ----------------

  const loadHealth = async () => {
    setLoadingHealth(true);
    try {
      const data = await adsHealth();
      setHealth(data || { status: "ok" });
    } catch (e) {
      console.error("[ADS HEALTH ERROR]", e);
      setHealth(null);
    } finally {
      setLoadingHealth(false);
    }
  };

  const loadBrands = async () => {
    setLoadingBrands(true);
    try {
      const data = await adsListBrands();
      const items = Array.isArray(data?.items) ? data.items : [];
      setBrands(items);

      const current = Number(brandId);
      const exists = current && items.some((b) => Number(getBrandId(b)) === current);

      if (!exists) {
        const firstValid = items.map(getBrandId).find((x) => x);
        setBrandId(firstValid ? String(firstValid) : "");
      }
    } catch (e) {
      console.error("[ADS BRANDS LOAD ERROR]", e);
      showToast(e?.message || "Failed to load Ads brands", "error");
    } finally {
      setLoadingBrands(false);
    }
  };

  const loadCompetitors = async (bid) => {
    const id = Number(bid || brandId);
    if (!id) { setCompetitors([]); setCompetitorEntities({}); return; }
    try {
      const data = await adsListCompetitors(id);
      const items = Array.isArray(data?.items) ? data.items : [];
      setCompetitors(items);
      loadCompetitorEntities(items);
    } catch {
      setCompetitors([]);
    }
  };

  const handleAddCompetitor = async (competitorBrandId) => {
    const id = Number(brandId);
    if (!id || !competitorBrandId) return;
    try {
      await adsAddCompetitor(id, Number(competitorBrandId));
      await loadCompetitors(id);
    } catch (e) {
      showToast(e?.message || "Failed to add competitor", "error");
    }
  };

  const handleRemoveCompetitor = async (competitorBrandId) => {
    const id = Number(brandId);
    if (!id || !competitorBrandId) return;
    try {
      await adsRemoveCompetitor(id, Number(competitorBrandId));
      setCompetitors(prev => prev.filter(c => c.competitor_brand_id !== competitorBrandId));
      setCompetitorEntities(prev => { const next = { ...prev }; delete next[competitorBrandId]; return next; });
    } catch (e) {
      showToast(e?.message || "Failed to remove competitor", "error");
    }
  };

  const loadCompetitorEntities = async (competitorList) => {
    const list = competitorList ?? competitors;
    if (!list.length) { setCompetitorEntities({}); return; }
    const results = await Promise.allSettled(
      list.map(c => adsListEntities(c.competitor_brand_id, null))
    );
    setCompetitorEntities(
      Object.fromEntries(
        list.map((c, i) => [
          c.competitor_brand_id,
          results[i].status === "fulfilled" && Array.isArray(results[i].value?.items)
            ? results[i].value.items
            : [],
        ])
      )
    );
  };

  const loadRuns = async (bid) => {
    const id = Number(bid || brandId);
    if (!id) {
      setRuns([]);
      return;
    }
    setLoadingRuns(true);
    try {
      const data = await adsListRuns(id);
      const items = Array.isArray(data?.items) ? data.items : [];
      setRuns(items);
    } catch (e) {
      console.error("[ADS RUNS LOAD ERROR]", e);
      showToast(e?.message || "Failed to load ingestion runs", "error");
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  };

  const loadLogs = async (runId) => {
    const rid = Number(runId);
    if (!rid) {
      setLogs([]);
      return;
    }
    setLoadingLogs(true);
    try {
      const data = await adsListLogs(rid);
      const items = Array.isArray(data?.items) ? data.items : [];
      setLogs(items);
    } catch (e) {
      console.error("[ADS LOGS LOAD ERROR]", e);
      showToast(e?.message || "Failed to load logs", "error");
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // IMPORTANT: DO NOT fetch entities filtered by provider.
  // Keep a single global "entities" list for the brand so provider cards don’t reset.
  const loadEntities = async (bid) => {
    const id = Number(bid || brandId);
    if (!id) {
      setEntities([]);
      return;
    }
    setLoadingEntities(true);
    try {
      const data = await adsListEntities(id, null);
      const items = Array.isArray(data?.items) ? data.items : [];
      setEntities(items);
    } catch (e) {
      console.error("[ADS ENTITIES LOAD ERROR]", e);
      showToast(e?.message || "Failed to load entities", "error");
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  const loadAds = async (bid, src, limit, offset, filters, includeComp) => {
    const id = Number(bid || brandId);
    const s = sanitizeSource(src || source);
    if (!id) {
      setAds([]);
      setAdsTotal(0);
      return;
    }
    const activeFilters = filters ?? currentFilterKeys();
    const useCompetitors = includeComp !== undefined ? includeComp : competitors.length > 0;
    setLoadingAds(true);
    try {
      const data = await adsListBrandAds(id, s, Number(limit ?? adsLimit), Number(offset ?? adsOffset), activeFilters, useCompetitors);
      const items = Array.isArray(data?.items) ? data.items : [];
      setAds(items);
      setAdsTotal(typeof data?.total === "number" ? data.total : items.length);
    } catch (e) {
      console.error("[ADS LIST LOAD ERROR]", e);
      showToast(e?.message || "Failed to load ads", "error");
      setAds([]);
      setAdsTotal(0);
    } finally {
      setLoadingAds(false);
    }
  };

  const loadStats = async (runId) => {
    const rid = Number(runId);
    if (!rid) {
      setRunStats(null);
      return;
    }
    setLoadingStats(true);
    try {
      const out = await adsGetRunStats(rid);
      setRunStats(out || null);
    } catch {
      setRunStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  // ---------------- Search ----------------

  const runSearch = async (q) => {
    const bid = Number(brandId);
    const src = sanitizeSource(source);
    const query = String(q || "").trim();

    if (!bid) return;

    if (!query) {
      setSearchItems([]);
      setSearchError(null);
      setSearchPage(0);
      return;
    }

    setLoadingSearch(true);
    setSearchError(null);

    try {
      const out = await adsSearchProviderEntities(src, bid, query, 50);
      const items = Array.isArray(out?.items) ? out.items : Array.isArray(out) ? out : [];
      setSearchItems(items);
      setSearchPage(0);
      setSearchOpen(true);
    } catch (e) {
      setSearchItems([]);
      setSearchError(e?.message || "Search not available yet.");
      setSearchPage(0);
    } finally {
      setLoadingSearch(false);
    }
  };

  useEffect(() => {
    const name = selectedBrand?.name ? String(selectedBrand.name) : "";
    setSearchQ(name);
    setSearchItems([]);
    setSearchError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.name]);

  // ---------------- initialization ----------------

  useEffect(() => {
    loadHealth();
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!brandId) return;
    loadRuns(brandId);
    loadEntities(brandId); // ✅ always load ALL entities for the brand
    loadCompetitors(brandId);
    setAdsOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // ✅ Provider switch should NOT refetch entities with a source filter.
  // Keep counts stable across cards; only reset paging.
  useEffect(() => {
    if (!brandId) return;
    setAdsOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  useEffect(() => {
    if (!brandId) return;
    if (!(step === 4 || adsFullscreen)) return;
    loadAds(brandId, source, adsLimit, adsOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, source, adsLimit, adsOffset, step, adsFullscreen, competitors.length]);

  useEffect(() => {
    const rid = lastRunForSource?.id;
    const shouldLoad = !!rid && (step >= 3 || showAdvancedLogs || showAdvancedRuns);
    if (shouldLoad) {
      loadLogs(rid);
      loadStats(rid);
    } else {
      setLogs([]);
      setRunStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRunForSource?.id, step, showAdvancedLogs, showAdvancedRuns]);

  useEffect(() => {
    if (step !== 4) return;
    setTimeout(() => {
      browseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [step]);

  // Auto-advance: on first load, if already fully configured, skip straight to step 4
  useEffect(() => {
    if (canProceedStep4 && !autoAdvancedRef.current && step < 4) {
      autoAdvancedRef.current = true;
      setStep(4);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canProceedStep4]);

  // ✅ Poll runs + live stats while executing so UI shows progress in real time
  useEffect(() => {
    if (!brandId) return;
    if (!starting && !executing) return;

    const t = setInterval(() => {
      loadRuns(brandId);
      if (currentRunId) loadStats(currentRunId);
    }, 1000);

    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, starting, executing, currentRunId]);

  // ---------------- actions ----------------

  const refreshAll = async () => {
    await Promise.allSettled([loadHealth(), loadBrands()]);
    if (brandId) {
      await Promise.allSettled([loadRuns(brandId), loadEntities(brandId)]);
      if (step === 4 || adsFullscreen) await Promise.allSettled([loadAds(brandId, source, adsLimit, adsOffset)]);
      if (lastRunForSource?.id && (step >= 3 || showAdvancedLogs || showAdvancedRuns)) {
        await Promise.allSettled([loadLogs(lastRunForSource.id), loadStats(lastRunForSource.id)]);
      }
    }
  };

  const upsertEntity = async (advertiser_ref, active, label, domain) => {
    const bid = Number(brandId);
    const src = sanitizeSource(source);
    const adv = String(advertiser_ref || "").trim();

    if (!bid) return showToast("Select a brand first.", "error");
    if (!adv) return showToast("Advertiser ID is required.", "error");

    setSavingEntity(true);
    try {
      const payload = { brand_id: bid, source: src, advertiser_ref: adv, active: !!active };
      if (label) payload.label = String(label).trim();
      if (domain) payload.domain = String(domain).trim();
      await adsUpsertEntity(payload);
      showToast(`Saved advertiser: ${adv}`, "success");
      setNewEntityRef("");

      await Promise.allSettled([loadEntities(bid)]);
      if (step === 4 || adsFullscreen) await Promise.allSettled([loadAds(bid, src, adsLimit, adsOffset)]);

      setTimeout(() => setStep((prev) => (prev < 2 ? 2 : prev)), 50);
    } catch (e) {
      showToast(e?.message || "Failed to save advertiser", "error");
    } finally {
      setSavingEntity(false);
    }
  };

  const deleteEntity = async (entityId) => {
    const id = Number(entityId);
    if (!id) return;

    const ok = window.confirm(
      "Delete this advertiser entity permanently?\n\nThis cannot be undone. (It will no longer be active/inactive - it will be removed.)"
    );
    if (!ok) return;

    setDeletingEntityId(id);
    try {
      await adsDeleteEntity(id);
      showToast(`Deleted entity #${id}`, "success");
      await Promise.allSettled([loadEntities(brandId)]);
      if (step === 4 || adsFullscreen) await Promise.allSettled([loadAds(brandId, source, adsLimit, adsOffset)]);
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    } finally {
      setDeletingEntityId(null);
    }
  };

  const validateEntity = async (advertiser_ref) => {
    const bid = Number(brandId);
    const src = sanitizeSource(source);
    const adv = String(advertiser_ref || "").trim();

    if (!bid) return showToast("Select a brand first.", "error");
    if (!adv) return showToast("Advertiser ID is required.", "error");

    const key = `${src}:${adv}`;
    setValidatingKey(key);

    try {
      const out = await adsValidateProvider(src, bid, adv);
      const fetched = out?.coverage?.fetched_items ?? out?.coverage?.fetched ?? out?.fetched_items ?? null;
      const extOk = out?.coverage?.sample_has?.external_ad_id;

      showToast(
        `Validation succeeded. Fetched: ${fetched ?? "?"}. External ad ID present: ${extOk === undefined ? "?" : extOk ? "Yes" : "No"}.`,
        "success"
      );

      await loadEntities(bid);
    } catch (e) {
      showToast(e?.message || "Validation failed.", "error");
      await loadEntities(bid);
    } finally {
      setValidatingKey(null);
    }
  };

  const bulkValidate = async ({ onlyUnvalidated = false } = {}) => {
    const bid = Number(brandId);
    const src = sanitizeSource(source);
    if (!bid) return showToast("Select a brand first.", "error");

    const list = (entitiesForSource || [])
      .filter((e) => !!e?.active)
      .filter((e) => {
        if (!onlyUnvalidated) return true;
        return e?.last_validated_at == null || e?.last_validation_ok == null;
      });

    if (list.length === 0) {
      return showToast(
        onlyUnvalidated ? "Nothing to validate (all active advertisers already validated)." : "No active advertisers to validate.",
        "warn"
      );
    }

    const ok = window.confirm(
      `Validate ${list.length} advertiser(s) now?\n\nThis will call the provider validation endpoint multiple times.`
    );
    if (!ok) return;

    setBulkValidating(true);
    setBulkProgress({ done: 0, total: list.length });

    let failures = 0;
    for (let i = 0; i < list.length; i++) {
      const adv = String(list[i]?.advertiser_ref || "").trim();
      const key = `${src}:${adv}`;
      setValidatingKey(key);

      try {
        // eslint-disable-next-line no-await-in-loop
        await adsValidateProvider(src, bid, adv);
      } catch {
        failures += 1;
      } finally {
        setBulkProgress({ done: i + 1, total: list.length });
      }
    }

    setValidatingKey(null);
    await loadEntities(bid);

    setBulkValidating(false);

    if (failures === 0) showToast(`Bulk validation complete - ${list.length}/${list.length} OK.`, "success");
    else showToast(`Bulk validation complete - ${list.length - failures} OK, ${failures} failed.`, "warn");
  };

  const startIngestionOnly = async () => {
    const bid = Number(brandId);
    if (!bid) return showToast("Select a brand first.", "error");

    const src = sanitizeSource(source);
    setStarting(true);

    try {
      const out = await adsStartIngestion(bid, src);
      const rid = Number(out?.run_id || 0) || null;

      setCurrentRunId(rid);
      setCurrentRunSource(src);

      showToast(`Run created (#${rid ?? "?"}).`, "success");
      await loadRuns(bid);
      setStep(3);
    } catch (e) {
      showToast(e?.message || "Failed to create run", "error");
    } finally {
      setStarting(false);
    }
  };

  const [ingestCompetitors, setIngestCompetitors] = useState(false);

  const executeRunById = async (runId, { dateFrom, dateTo, includeCompetitors } = {}) => {
    const rid = Number(runId);
    if (!rid) return showToast("No run to execute.", "error");

    setExecuting(true);
    try {
      const out = await adsExecuteIngestion(rid, { dateFrom, dateTo, includeCompetitors });

      showToast(
        `Execution finished. Status: ${out?.status ?? "?"}. Items fetched: ${out?.fetched_items ?? out?.coverage?.fetched_items ?? "?"}.`,
        "success"
      );

      await Promise.allSettled([
        loadRuns(brandId),
        step >= 3 || showAdvancedLogs ? loadLogs(rid) : Promise.resolve(),
        step >= 3 || showAdvancedRuns ? loadStats(rid) : Promise.resolve(),
      ]);

      if (step === 4 || adsFullscreen) await Promise.allSettled([loadAds(brandId, source, adsLimit, adsOffset)]);

      setStep(4);
    } catch (e) {
      showToast(e?.message || "Execution failed.", "error");
    } finally {
      setExecuting(false);
    }
  };

  const runNowCreateAndExecute = async () => {
    const bid = Number(brandId);
    const src = sanitizeSource(source);

    if (!bid) return showToast("Select a brand first.", "error");
    if (validatedActiveEntities.length === 0) {
      return showToast("Quality gate: you need at least 1 ACTIVE advertiser that is VALIDATED OK before running ingestion.", "error");
    }

    setStarting(true);
    setExecuting(true);

    try {
      const started = await adsStartIngestion(bid, src);
      const rid = Number(started?.run_id) || null;

      setCurrentRunId(rid);
      setCurrentRunSource(src);

      showToast(`Run created (#${rid || "?"}). Executing…`, "info");
      await loadRuns(bid);

      if (!rid) throw new Error("Server did not return run_id.");
      await executeRunById(rid, { dateFrom: fetchDateFrom || undefined, dateTo: fetchDateTo || undefined, includeCompetitors: ingestCompetitors });
    } catch (e) {
      showToast(e?.message || "Run failed.", "error");
    } finally {
      setStarting(false);
      setExecuting(false);
    }
  };

  const finalizeRun = async (status) => {
    const rid = Number(lastRunForSource?.id);
    if (!rid) return showToast("No run to finalize.", "error");

    setFinalizing(true);
    try {
      await adsFinalizeRun(rid, status, status === "failed" ? "Manually finalized from UI." : null);
      showToast(`Run #${rid} finalized (${status}).`, "success");
      await Promise.allSettled([loadRuns(brandId), loadLogs(rid), loadStats(rid)]);
    } catch (e) {
      showToast(e?.message || "Failed to finalize run", "error");
    } finally {
      setFinalizing(false);
    }
  };

  const openAdDrawer = async (adId) => {
    const id = Number(adId);
    if (!id) return;

    setSelectedAdId(id);
    setSelectedAd(null);
    setDrawerOpen(true);
    setLoadingAd(true);

    try {
      const data = await adsGetAd(id);
      setSelectedAd(data || null);
    } catch (e) {
      console.error("[ADS GET AD ERROR]", e);
      showToast(e?.message || "Failed to load ad", "error");
      setSelectedAd(null);
    } finally {
      setLoadingAd(false);
    }
  };

  // Reload the page of ads being looked at, keeping offset and filters. Used
  // after a refetch so the card and list show the updated ad.
  const reloadCurrentAds = () => loadAds(brandId, source, adsLimit, adsOffset, currentFilterKeys());

  const _saveFiltersToLS = (patch) => {
    try {
      const s = JSON.parse(localStorage.getItem(LS) || "{}");
      localStorage.setItem(LS, JSON.stringify({ ...s, ...patch }));
    } catch {}
  };

  const applyFilters = (override = null) => {
    // One definition of "what is filtered", shared with the list load and the
    // CSV export. The inline copy that used to live here drifted from it and
    // referenced a helper that no longer existed.
    // `override` lets a caller apply a filter in the same tick it sets it,
    // rather than waiting a render for the state to land.
    const f = { ...currentFilterKeys(), ...(override || {}) };
    _saveFiltersToLS({ fieldFilters: _compactFieldFilters(fieldFilters) || {}, filterNewDays, filterAsOf, filterNotUpdatedSince, filterRunningFrom, filterRunningTo });
    setAdsOffset(0);
    loadAds(brandId, source, adsLimit, 0, f);
  };

  const saveCurrentFilter = (name) => {
    const entry = {
      id: Date.now(),
      name: name.trim(),
      filters: {
        fieldFilters: _compactFieldFilters(fieldFilters) || {},
        newDays: filterNewDays || "",
        asOf: filterAsOf || "",
        notUpdatedSince: filterNotUpdatedSince || "",
        runningFrom: filterRunningFrom || "",
        runningTo: filterRunningTo || "",
      },
    };
    const next = [...savedFilters, entry];
    setSavedFilters(next);
    _persistSavedFilters(next);
  };

  const deleteSavedFilter = (id) => {
    const next = savedFilters.filter(sf => sf.id !== id);
    setSavedFilters(next);
    _persistSavedFilters(next);
  };

  const applySavedFilter = (saved) => {
    const f = saved.filters || {};
    // Presets saved before the catalogue-driven panel hold named keys.
    const ff = f.fieldFilters || legacyToFieldFilters(f);
    const days = f.newDays || "";
    setFieldFilters(ff);
    setFilterNewDays(days);
    setFilterAsOf(f.asOf || "");
    setFilterNotUpdatedSince(f.notUpdatedSince || "");
    setFilterRunningFrom(f.runningFrom || "");
    setFilterRunningTo(f.runningTo || "");
    _saveFiltersToLS({ fieldFilters: ff, filterNewDays: days, filterAsOf: f.asOf || "", filterNotUpdatedSince: f.notUpdatedSince || "",
                       filterRunningFrom: f.runningFrom || "", filterRunningTo: f.runningTo || "" });
    setAdsOffset(0);
    loadAds(brandId, source, adsLimit, 0, {
      fieldFilters: _compactFieldFilters(ff),
      newSince: days ? new Date(Date.now() - Number(days) * 86400000).toISOString() : undefined,
      asOf: f.asOf || undefined,
      notUpdatedSince: f.notUpdatedSince || undefined,
      runningFrom: f.runningFrom || undefined,
      runningTo: f.runningTo || undefined,
    });
  };

  const clearFilters = () => {
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterEndDateFrom("");
    setFilterEndDateTo("");
    setFilterStatus("");
    setFilterCreativeType("");
    setFilterHeadline("");
    setFilterAdvertiser("");
    setFilterAdvertiserName("");
    setFilterCta("");
    setFilterBody("");
    setFilterVisualPalette("");
    setFilterVisualFunnel("");
    setFilterVisualClaim("");
    setFilterPeoplePresent("");
    setFilterNewDays("");
    setFilterPublisherPlatform(""); setFilterLanguage(""); setFilterTargetGender("");
    setFilterEuReachMin(""); setFilterTargetDomain(""); setFilterGooglePlatform("");
    setFieldFilters({});
    setFilterAsOf("");
    setFilterNotUpdatedSince("");
    setFilterRunningFrom("");
    setFilterRunningTo("");
    _saveFiltersToLS({ fieldFilters: {}, filterAsOf: "", filterNotUpdatedSince: "", filterRunningFrom: "", filterRunningTo: "" });
    _saveFiltersToLS({ filterDateFrom: "", filterDateTo: "", filterEndDateFrom: "", filterEndDateTo: "", filterStatus: "", filterCreativeType: "", filterHeadline: "", filterAdvertiser: "", filterAdvertiserName: "", filterCta: "", filterBody: "", filterVisualPalette: "", filterVisualFunnel: "", filterVisualClaim: "", filterPeoplePresent: "", filterNewDays: "", filterPublisherPlatform: "", filterLanguage: "", filterTargetGender: "", filterEuReachMin: "", filterTargetDomain: "", filterGooglePlatform: "" });
    setAdsOffset(0);
    loadAds(brandId, source, adsLimit, 0, {});
  };

  const preloadSnapshots = async (runId) => {
    const rid = Number(runId || lastRunForSource?.id);
    if (!rid) return showToast("No run available to preload.", "warn");
    try {
      const out = await adsPreloadSnapshots(rid);
      const n = out?.queued ?? 0;
      if (n === 0) showToast("No new snapshots to preload (all already cached).", "info");
      else showToast(`Queued ${n} snapshot capture${n === 1 ? "" : "s"} in the background.`, "success");
    } catch (e) {
      showToast(e?.message || "Preload failed.", "error");
    }
  };

  const exportCsv = async ({ fields = null, allFields = false } = {}) => {
    const id = Number(brandId);
    const s = sanitizeSource(source);
    if (!id) return showToast("Select a brand first.", "error");

    // Rows follow the list view: same filter state, same competitor scope.
    // Columns follow the field picker unless every field was requested.
    const filters = currentFilterKeys();
    const useCompetitors = competitors.length > 0;

    try {
      const blob = await adsExportBrandAdsCsv(id, s, filters, useCompetitors, { fields, allFields });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ads_export_${id}_${s}${allFields ? "_all" : ""}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || "Export failed.", "error");
    }
  };

  const nextPage = () => setAdsOffset((x) => x + Number(adsLimit));
  const prevPage = () => setAdsOffset((x) => Math.max(0, x - Number(adsLimit)));

  // hard gate: no jumping
  useEffect(() => {
    if (step > maxStepAllowed) setStep(maxStepAllowed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxStepAllowed]);

  // provider snapshot for dashboard cards (provider-level counts independent of selected provider)
  const providers = useMemo(
    () => [
      { key: "meta", title: "Meta", icon: "Ⓜ️", subtitle: "Ad Library / Graph fetch. Best coverage." },
      { key: "google", title: "Google", icon: "🟦", subtitle: "SerpApi-based. Needs clean tokens/domains." },
      { key: "tiktok", title: "TikTok", icon: "🎵", subtitle: "TikTok Ad Library via SearchAPI. Videos stored on our side." },
    ],
    []
  );

  const providerSnapshot = useMemo(() => {
    const out = {};
    for (const p of providers) {
      const src = p.key;

      const list = entitiesBySource[src] || [];
      const active = list.filter((e) => !!e?.active);
      const validated = active.filter((e) => e?.last_validation_ok === true);

      const lastRun = (runsSorted || []).find((r) => String(r?.source || "").toLowerCase() === src) || null;

      const cfg =
        health?.providers?.[src]?.configured ??
        health?.providers?.[src]?.ok ??
        health?.[src]?.configured ??
        health?.[`${src}_configured`] ??
        null;

      const configuredLabel =
        cfg === true ? "Configured" : cfg === false ? "Not configured" : apiStatus === "ok" ? "Unknown config" : "API issues";

      const configuredTone = cfg === true ? "success" : cfg === false ? "error" : apiStatus === "ok" ? "neutral" : "warn";

      out[src] = { activeCount: active.length, validatedCount: validated.length, lastRun, configuredLabel, configuredTone };
    }
    return out;
  }, [providers, entitiesBySource, runsSorted, health, apiStatus]);

  const selectProviderAnd = (src, fn) => {
    const s = sanitizeSource(src);
    setSource(s);
    setTimeout(() => fn?.(s), 0);
  };

  const deleteAds = async (adIds) => {
    try {
      const res = await adsDeleteAdsBatch(adIds);
      showToast(`Deleted ${res.deleted} ad${res.deleted !== 1 ? "s" : ""}`);
      loadAds();
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    }
  };

  return {
    // refs
    browseRef,

    // state
    step,
    setStep,
    maxStepAllowed,

    health,
    loadingHealth,
    apiStatus,
    apiTone,

    brands,
    loadingBrands,
    brandId,
    setBrandId,
    selectedBrand,
    competitors,
    loadCompetitors,
    handleAddCompetitor,
    handleRemoveCompetitor,
    competitorEntities,
    loadCompetitorEntities,

    source,
    setSource,

    runs,
    runsSorted,
    loadingRuns,
    lastRunForSource,

    logs,
    loadingLogs,

    entities,
    entitiesBySource,
    entitiesForSource,
    loadingEntities,
    newEntityRef,
    setNewEntityRef,
    savingEntity,
    deletingEntityId,

    validatingKey,
    bulkValidating,
    bulkProgress,

    searchQ,
    setSearchQ,
    searchItems,
    loadingSearch,
    searchError,
    searchOpen,
    setSearchOpen,
    searchPage,
    setSearchPage,

    starting,
    executing,
    finalizing,

    // ✅ run visibility while executing
    currentRunId,
    currentRunSource,

    runStats,
    loadingStats,

    ads,
    loadingAds,
    adsLimit,
    setAdsLimit,
    adsOffset,
    setAdsOffset,
    adsTotal,
    adsFullscreen,
    setAdsFullscreen,
    viewMode,
    setViewMode,
    filterDateFrom,
    setFilterDateFrom,
    filterDateTo,
    setFilterDateTo,
    filterEndDateFrom,
    setFilterEndDateFrom,
    filterEndDateTo,
    setFilterEndDateTo,
    filterStatus,
    setFilterStatus,
    filterCreativeType,
    setFilterCreativeType,
    filterHeadline,
    setFilterHeadline,
    filterAdvertiser,
    setFilterAdvertiser,
    filterAdvertiserName,
    setFilterAdvertiserName,
    filterCta,
    setFilterCta,
    filterBody,
    setFilterBody,
    filterVisualPalette,
    setFilterVisualPalette,
    filterVisualFunnel,
    setFilterVisualFunnel,
    filterVisualClaim,
    setFilterVisualClaim,
    filterPeoplePresent,
    setFilterPeoplePresent,
    filterNewDays,
    filterAsOf, setFilterAsOf,
    filterNotUpdatedSince, setFilterNotUpdatedSince,
    filterRunningFrom, setFilterRunningFrom,
    filterRunningTo, setFilterRunningTo,
    setFilterNewDays,
    filterPublisherPlatform,
    setFilterPublisherPlatform,
    filterLanguage,
    setFilterLanguage,
    filterTargetGender,
    setFilterTargetGender,
    filterEuReachMin,
    setFilterEuReachMin,
    filterTargetDomain,
    setFilterTargetDomain,
    filterGooglePlatform,
    filterIngestionRunId,
    fieldFilters,
    setFieldFilters,
    reloadCurrentAds,
    setFilterIngestionRunId,
    setFilterGooglePlatform,
    applyFilters,
    clearFilters,
    savedFilters,
    saveCurrentFilter,
    deleteSavedFilter,
    applySavedFilter,

    drawerOpen,
    setDrawerOpen,
    selectedAdId,
    setSelectedAdId,
    selectedAd,
    setSelectedAd,
    loadingAd,

    showAdvancedRuns,
    setShowAdvancedRuns,
    showAdvancedLogs,
    setShowAdvancedLogs,
    showFinalize,
    setShowFinalize,

    // derived
    activeEntities,
    validatedActiveEntities,
    canProceedStep2,
    canProceedStep3,
    canProceedStep4,
    hasSuccessfulRunForSource,

    // actions
    refreshAll,
    loadAds,
    loadEntities,
    loadRuns,
    loadLogs,
    loadStats,
    runSearch,
    upsertEntity,
    deleteEntity,
    validateEntity,
    fetchDateFrom,
    setFetchDateFrom,
    fetchDateTo,
    setFetchDateTo,
    bulkValidate,
    startIngestionOnly,
    executeRunById,
    runNowCreateAndExecute,
    finalizeRun,
    openAdDrawer,
    preloadSnapshots,
    exportCsv,
    nextPage,
    prevPage,
    providerSnapshot,
    providers,
    selectProviderAnd,
    deleteAds,
    ingestCompetitors,
    setIngestCompetitors,
  };
}